import React, { useEffect, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ProjectFilterService from 'service/ClientTransmoraService/ProjectFilterService';
import LoadingSpinner from 'component/LoadingSpinner';

interface FilterValueItem {
  id: string | null;
  code: string;
  name: string;
}

interface FilterItem {
  id: string | null;
  name: string;
  filterKey: string;
  filterValue: FilterValueItem[];
  collapsed: boolean;
}

interface DeleteTarget {
  type: 'filter' | 'value';
  filterIndex: number;
  valueIndex?: number;
  label: string;
}

const ProjectFilterForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useSelector((state: any) => state.auth);
  const projectFilterService = ProjectFilterService();

  const [filters, setFilters] = useState<FilterItem[]>([]);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isLoadingSave, setIsLoadingSave] = useState(false);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);

  const fetchDetail = async () => {
    const token = profile?.data.token;
    if (!token || !id) return;

    setIsLoadingDetail(true);
    try {
      const res = await projectFilterService.ProjectFilterDetail(token, id);
      const list = res.data?.filter ?? [];
      setFilters(list.map((f: any) => ({
        id: f.id,
        name: f.name,
        filterKey: f.filterKey,
        collapsed: true,
        filterValue: (f.filterValue ?? []).map((fv: any) => ({
          id: fv.id,
          code: fv.code,
          name: fv.name,
        })),
      })));
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoadingDetail(false);
    }
  };

  useEffect(() => {
    fetchDetail();
  }, []);

  // ── Filter handlers ──
  const handleAddFilter = () => {
    setFilters((prev) => [
      ...prev,
      { id: null, name: '', filterKey: '', filterValue: [], collapsed: false },
    ]);
  };

  const handleFilterChange = (index: number, field: 'name' | 'filterKey', value: string) => {
    setFilters((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const handleToggleCollapse = (index: number) => {
    setFilters((prev) =>
      prev.map((item, i) => (i === index ? { ...item, collapsed: !item.collapsed } : item))
    );
  };

  // ── Filter Value handlers ──
  const handleAddValue = (filterIndex: number) => {
    setFilters((prev) =>
      prev.map((item, i) =>
        i === filterIndex
          ? {
              ...item,
              collapsed: false,
              filterValue: [...item.filterValue, { id: null, code: '', name: '' }],
            }
          : item
      )
    );
  };

  const handleValueChange = (filterIndex: number, valueIndex: number, field: 'code' | 'name', value: string) => {
    setFilters((prev) =>
      prev.map((item, i) =>
        i === filterIndex
          ? {
              ...item,
              filterValue: item.filterValue.map((fv, vi) =>
                vi === valueIndex ? { ...fv, [field]: value } : fv
              ),
            }
          : item
      )
    );
  };

  // ── Delete modal ──
  const handleDeleteFilterClick = (filterIndex: number) => {
    setDeleteTarget({
      type: 'filter',
      filterIndex,
      label: filters[filterIndex].name || `Filter #${filterIndex + 1}`,
    });
    setShowDeleteModal(true);
  };

  const handleDeleteValueClick = (filterIndex: number, valueIndex: number) => {
    const fv = filters[filterIndex].filterValue[valueIndex];
    setDeleteTarget({
      type: 'value',
      filterIndex,
      valueIndex,
      label: fv.name || fv.code || `Value #${valueIndex + 1}`,
    });
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;

    if (deleteTarget.type === 'filter') {
      setFilters((prev) => prev.filter((_, i) => i !== deleteTarget.filterIndex));
    } else if (deleteTarget.type === 'value' && deleteTarget.valueIndex !== undefined) {
      setFilters((prev) =>
        prev.map((item, i) =>
          i === deleteTarget.filterIndex
            ? {
                ...item,
                filterValue: item.filterValue.filter((_, vi) => vi !== deleteTarget.valueIndex),
              }
            : item
        )
      );
    }

    setShowDeleteModal(false);
    setDeleteTarget(null);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteTarget(null);
  };

  // ── Submit ──
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = profile?.data.token;
    if (!token || !id) return;

    setIsLoadingSave(true);
    try {
      const payload = {
        projectId: id,
        filter: filters.map((item) => ({
          id: item.id || '',
          name: item.name,
          filterKey: item.filterKey,
          filterValue: item.filterValue.map((fv) => ({
            id: fv.id || '',
            code: fv.code,
            name: fv.name,
          })),
        })),
      };

      await projectFilterService.ProjectFilterSave(token, payload);
      alert('Project Filter berhasil disimpan.');
      navigate('/client-project-filter');
    } catch (err: any) {
      alert(err.message || 'Gagal menyimpan data.');
      console.error(err);
    } finally {
      setIsLoadingSave(false);
    }
  };

  return (
    <div className="pt-1 h-100" style={{ background: '#f2f4f8' }}>
      <div className="m-4">
        <h4 className="mb-4">Manage Project Filter</h4>
        <div className="p-4" style={{ background: 'white' }}>
          {isLoadingDetail ? (
            <LoadingSpinner />
          ) : (
            <form onSubmit={handleSubmit}>

              {/* Add Filter Button */}
              <div className="d-flex justify-content-end mb-3">
                <button
                  type="button"
                  className="btn btn-primary px-4"
                  onClick={handleAddFilter}
                >
                  Add Filter
                </button>
              </div>

              {/* Filter Cards */}
              <div className="mb-4">
                {filters.length === 0 && (
                  <p className="text-muted text-center fst-italic py-4">
                    Belum ada filter. Klik "Add Filter" untuk menambahkan.
                  </p>
                )}

                {filters.map((item, filterIndex) => (
                  <div key={filterIndex} className="d-flex align-items-start mb-3">

                    {/* Grip placeholder — tidak draggable, hanya visual */}
                    <div className="me-2 text-secondary mt-3" style={{ fontSize: '18px', paddingTop: '6px' }}>
                      <i className="fa-solid fa-grip-vertical"></i>
                    </div>

                    {/* Card */}
                    <div
                      className="flex-grow-1 p-3"
                      style={{ border: '1px solid #ccc', borderRadius: '10px', background: 'white' }}
                    >
                      {/* Row: Name + Filter Key + Delete */}
                      <div className="d-flex align-items-end gap-3">
                        <div className="flex-grow-1">
                          <label className="form-label fw-semibold mb-1" style={{ fontSize: '13px' }}>Name</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={item.name}
                            onChange={(e) => handleFilterChange(filterIndex, 'name', e.target.value)}
                            required
                          />
                        </div>
                        <div style={{ width: '280px' }}>
                          <label className="form-label fw-semibold mb-1" style={{ fontSize: '13px' }}>Filter Key</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={item.filterKey}
                            onChange={(e) => handleFilterChange(filterIndex, 'filterKey', e.target.value)}
                            required
                          />
                        </div>
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm border-0 mb-1"
                          onClick={() => handleDeleteFilterClick(filterIndex)}
                          title="Hapus filter"
                        >
                          <i className="fa-solid fa-trash"></i>
                        </button>
                      </div>

                      {/* Collapse toggle + Add Value */}
                      <div className="d-flex align-items-center justify-content-between mt-2">
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 text-decoration-none fw-semibold"
                          onClick={() => handleToggleCollapse(filterIndex)}
                          style={{ fontSize: '13px' }}
                        >
                          {item.filterValue.length} Values{' '}
                          <i className={`fa-solid fa-chevron-${item.collapsed ? 'down' : 'up'} ms-1`} style={{ fontSize: '11px' }}></i>
                        </button>

                        {!item.collapsed && (
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 text-decoration-none"
                            onClick={() => handleAddValue(filterIndex)}
                            style={{ fontSize: '13px' }}
                            title="Tambah value"
                          >
                            <i className="fa-solid fa-plus me-1"></i>Add Value
                          </button>
                        )}
                      </div>

                      {/* Filter Values */}
                      {!item.collapsed && (
                        <div className="mt-2">
                          {item.filterValue.length === 0 && (
                            <p className="text-muted fst-italic mb-0" style={{ fontSize: '12px' }}>
                              Belum ada value. Klik "+ Add Value" untuk menambahkan.
                            </p>
                          )}
                          {item.filterValue.map((fv, valueIndex) => (
                            <div key={valueIndex} className="d-flex align-items-end gap-2 mb-2">
                              <div style={{ width: '200px' }}>
                                {valueIndex === 0 && (
                                  <label className="form-label mb-1" style={{ fontSize: '12px' }}>Code</label>
                                )}
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={fv.code}
                                  onChange={(e) => handleValueChange(filterIndex, valueIndex, 'code', e.target.value)}
                                  placeholder="Code"
                                  required
                                />
                              </div>
                              <div style={{ width: '280px' }}>
                                {valueIndex === 0 && (
                                  <label className="form-label mb-1" style={{ fontSize: '12px' }}>Name</label>
                                )}
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={fv.name}
                                  onChange={(e) => handleValueChange(filterIndex, valueIndex, 'name', e.target.value)}
                                  placeholder="Name"
                                  required
                                />
                              </div>
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm border-0"
                                onClick={() => handleDeleteValueClick(filterIndex, valueIndex)}
                                title="Hapus value"
                              >
                                <i className="fa-solid fa-trash"></i>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="w-100 d-flex justify-content-end">
                <Link to="/client-project-filter">
                  <button type="button" className="btn btn-outline-primary">
                    Cancel
                  </button>
                </Link>
                <button
                  type="submit"
                  className="btn btn-primary d-flex justify-content-center align-items-center ms-2"
                  disabled={isLoadingSave}
                >
                  {isLoadingSave && (
                    <>
                      <LoadingSpinner reverse={true} />
                      <p className="ms-2"></p>
                    </>
                  )}
                  Save
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deleteTarget && (
        <div
          className="modal fade show"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.4)' }}
          onClick={handleCloseDeleteModal}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content p-4">
              <h5 className="fw-bold mb-1">
                Hapus {deleteTarget.type === 'filter' ? 'Filter' : 'Value'}
              </h5>
              <hr />
              <p className="mb-0">
                Apakah Anda yakin ingin menghapus{' '}
                {deleteTarget.type === 'filter' ? 'filter' : 'value'}{' '}
                <strong>"{deleteTarget.label}"</strong>?
              </p>
              {deleteTarget.type === 'filter' && (
                <p className="text-danger small mt-1 mb-0">
                  Semua value di dalam filter ini juga akan ikut terhapus.
                </p>
              )}

              <div className="d-flex justify-content-end gap-2 mt-4">
                <button
                  className="btn btn-outline-secondary px-4"
                  onClick={handleCloseDeleteModal}
                  style={{ borderRadius: '8px' }}
                >
                  Batal
                </button>
                <button
                  className="btn btn-danger px-4"
                  onClick={handleConfirmDelete}
                  style={{ borderRadius: '8px' }}
                >
                  <i className="fa-solid fa-trash me-2"></i>
                  Hapus
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectFilterForm;